import { Box, Collapse, darken, Divider, lighten, Stack } from "@mui/material";
import { useSSTheme } from "../../reusable/hooks/useSSTheme";
import Image from "next/image";
import { SSText } from "src/components/base/components/SSText";
import { TAddOn, TEvent } from "src/shared/types/globalStotre.types";
import React, { useContext, createContext, use } from "react";
import { formatSSDate } from "src/utils/utils";
import { EventTags } from "./EventTags";
import { EventCardSingleGames } from "./EventCardSingleGames";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import placeHolderImage from "public/images/empty_state_image.png";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { EventCardSeatInformation } from "./EventCardSeatInformation";
import {
  EventCardFooter,
  EventCardMarketplaceNotifications,
  ShowOffersButton,
} from "./EventCard.components";
import { EventCardAttendanceNotification } from "./event-attendance/EventAttendaceWidget.components";

type TEventCardProps = {
  isDarkBg?: boolean;
  hideTags?: boolean;
  event: TEvent;
  showFooter?: boolean;
  showSingleGames?: boolean;
  showSeatCount?: boolean;
  showSellTicketsButton?: boolean;
  showAttendanceNotification?: boolean;
  showPriceBreakdown?: boolean;
  renderFooter?: () => React.ReactNode;
};

interface TEventCardCtx extends TEventCardProps {
  isAddOnsOpen: boolean;
  setIsAddOnsOpen: (isOpen: boolean) => void;
  isSingleGamesOpen: boolean;
  setIsSingleGamesOpen: (isOpen: boolean) => void;
  singleGames: TAddOn[];
  addOns: TAddOn[];
  colors: {
    bgColor: string;
    borderColor: string;
    textColor: string;
    addOnButtonColorScheme: string;
    addOnGroupTagBgColor: string;
    sellTicketsButtonColorScheme: string;
  };
}

const EventCardCtx = createContext<TEventCardCtx | null>(null);
export const useEventCardCtx = () => useContext(EventCardCtx) as TEventCardCtx;

// Per-event reservation pricing breakdown for paid events. Opt-in via the
// `showPriceBreakdown` prop — shown in the reserve/exchange modals, NOT on the
// upcoming-events card. Renders only when the event is paid and at least one
// pricing value is set (null values are "unpriced" and skipped, so we never
// show $0.00 for a fee that simply wasn't configured).
const EventPriceBreakdown: React.FC = () => {
  const ctx = useEventCardCtx();
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const { event } = ctx;

  if (!event?.isPaymentRequired) return null;

  const { price, serviceFee, additionalFee } = event;
  const hasAnyPrice =
    price != null || serviceFee != null || additionalFee != null;
  if (!hasAnyPrice) return null;

  const total =
    (price ?? 0) + (serviceFee ?? 0) + (additionalFee ?? 0);

  // All-in pricing: the fees are already baked into `total`, so listing them
  // again as their own rows would read as an extra charge on top. Collapse the
  // box down to the single all-in amount.
  const isAllInPricing = Boolean(
    HOME_PAGE_SETTINGS?.EVENT_CARD?.ADD_SERVICE_FEE_TO_PRICE
  );

  const rows: { label: string; value: number }[] = [];
  if (!isAllInPricing) {
    if (price != null) rows.push({ label: "Price", value: price });
    if (serviceFee != null)
      rows.push({ label: "Service Fee", value: serviceFee });
    if (additionalFee != null)
      rows.push({ label: "Additional Fee", value: additionalFee });
  }

  const money = (n: number) => `$${n.toFixed(2)}`;

  return (
    <Box px={1.5} pb={1.5}>
      <Stack
        spacing={0.5}
        p={1.5}
        sx={{
          border: `1px solid ${ctx.colors.borderColor}`,
          borderRadius: "8px",
        }}
      >
        {rows.map((row) => (
          <Stack
            key={row.label}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <SSText
              variant="body"
              fontSize={"0.9rem"}
              color={ctx.colors.textColor}
              sx={{ opacity: 0.7 }}
            >
              {row.label}
            </SSText>
            <SSText
              variant="body"
              fontSize={"0.9rem"}
              fontWeight={500}
              color={ctx.colors.textColor}
            >
              {money(row.value)}
            </SSText>
          </Stack>
        ))}

        {rows.length > 0 ? (
          <Divider sx={{ backgroundColor: ctx.colors.borderColor, my: 0.5 }} />
        ) : null}

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <SSText
            variant="body"
            fontSize={"0.95rem"}
            fontWeight={700}
            color={ctx.colors.textColor}
          >
            Total
          </SSText>
          <SSText
            variant="body"
            fontSize={"0.95rem"}
            fontWeight={700}
            color={ctx.colors.textColor}
          >
            {money(total)} / seat
          </SSText>
        </Stack>
      </Stack>
    </Box>
  );
};

const withEventCardCtx = (Component: React.FC<TEventCardProps>) => {
  return function WrappedWithCtx(props: TEventCardProps) {
    const theme = useSSTheme();
    const [isAddOnsOpen, setIsAddOnsOpen] = React.useState(false);
    const [isSingleGamesOpen, setIsSingleGamesOpen] = React.useState(false);
    const singleGames = props?.event?.addOns?.filter((itm) => itm.productType == "SINGLE GAME" || itm.productType == "UPGRADES WITHOUT EXCHANGE"); // prettier-ignore
    const addOns = props?.event?.addOns?.filter((itm) => itm.productType !== "SINGLE GAME" && itm.productType !== "UPGRADES WITHOUT EXCHANGE"); // prettier-ignore

    return (
      <EventCardCtx.Provider
        value={{
          ...props,
          isAddOnsOpen,
          setIsAddOnsOpen,
          isSingleGamesOpen,
          setIsSingleGamesOpen,
          singleGames,
          addOns,
          colors: {
            bgColor: props?.isDarkBg
              ? theme.colors.common.black
              : theme.colors.common.white,
            borderColor: props.isDarkBg
              ? isAddOnsOpen
                ? darken(theme.colors.teamSecondary, 0.5)
                : theme.colors.gray[300]
              : isAddOnsOpen
              ? theme.colors.teamSecondary
              : theme.colors.border,
            textColor: props.isDarkBg
              ? theme.colors.common.white
              : theme.colors.common.black,
            addOnButtonColorScheme: props.isDarkBg
              ? theme.colors.border
              : lighten(theme.colors.gray[200], 0.4),
            addOnGroupTagBgColor: props.isDarkBg
              ? theme.colors.gray[500]
              : theme.colors.common.black,
            sellTicketsButtonColorScheme: props.isDarkBg
              ? theme.colors.border
              : theme.colors.teamPrimary,
          },
        }}
      >
        <Component {...props} />
      </EventCardCtx.Provider>
    );
  };
};

export const EventCard = withEventCardCtx((props: TEventCardProps) => {
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const eventCardCtx = useEventCardCtx();
  const { md: mdUp } = useCssMediaQuery("up");

  return (
    <Box
      border={`1px solid ${eventCardCtx.colors.borderColor}`}
      borderRadius={2.5}
      position={"relative"}
      bgcolor={eventCardCtx.colors.bgColor}
    >
      {!props.hideTags ? (
        <Stack
          position={"absolute"}
          direction={"row"}
          justifyContent={"flex-end"}
          width={"100%"}
          spacing={1}
          px={1}
          sx={{
            transform: `translateY(-50%)`,
          }}
        >
          <EventTags event={props.event} />
        </Stack>
      ) : null}

      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={1}
        p={1.5}
        py={2.5}
      >
        <Box width={"48px"} height={"48px"}>
          <Image
            alt={`${props?.event?.title} logo`}
            src={props?.event?.logoUrl || placeHolderImage.src}
            width={48}
            height={48}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>

        <Box textAlign={"left"}>
          <SSText
            variant="h6"
            color={eventCardCtx.colors.textColor}
            fontWeight={700}
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: "0.3em",
            }}
          >
            {props?.event?.title}
          </SSText>
          <Stack
            mt={0.8}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"left"}
            sx={{ opacity: 0.7 }}
          >
            <SSText
              variant="body"
              color={eventCardCtx.colors.textColor}
              fontSize={`0.9rem`}
              fontWeight={500}
            >
              {formatSSDate(props.event.date, "EEEE, MMM dd")}
            </SSText>
            <Divider
              orientation="vertical"
              sx={{
                height: "0.8rem",
                mx: 0.8,
                backgroundColor: eventCardCtx.colors.textColor,
              }}
            />
            <SSText
              variant="body"
              color={eventCardCtx.colors.textColor}
              fontSize={`0.9rem`}
              fontWeight={500}
            >
              {props.event.time}
            </SSText>
          </Stack>
        </Box>

        {props?.showSingleGames &&
        eventCardCtx?.singleGames?.length &&
        HOME_PAGE_SETTINGS?.EVENT_CARD?.SHOW_SINGLE_GAMES_IN_COLLAPSE &&
        mdUp ? (
          <>
            <Box flexGrow={1} />
            <Box maxWidth={"140px"}>
              <ShowOffersButton />
            </Box>
          </>
        ) : null}
      </Stack>

      {props.showPriceBreakdown ? <EventPriceBreakdown /> : null}

      {props.showSeatCount && eventCardCtx?.event?.seatInformation ? (
        <>
          <EventCardSeatInformation />
          <EventCardMarketplaceNotifications />
        </>
      ) : null}

      {props?.showAttendanceNotification ? (
        <EventCardAttendanceNotification />
      ) : null}

      {props.showSingleGames &&
      eventCardCtx?.singleGames.length &&
      HOME_PAGE_SETTINGS?.EVENT_CARD?.SHOW_SINGLE_GAMES_IN_COLLAPSE ? (
        <Stack
          px={mdUp ? 3 : 2}
          pb={eventCardCtx?.isSingleGamesOpen ? (mdUp ? 3 : 2) : 0}
          spacing={2}
        >
          {!mdUp ? <ShowOffersButton /> : null}
          <Collapse in={eventCardCtx.isSingleGamesOpen}>
            <EventCardSingleGames />
          </Collapse>
        </Stack>
      ) : null}

      {props.showSingleGames &&
      eventCardCtx?.singleGames?.length &&
      !HOME_PAGE_SETTINGS?.EVENT_CARD?.SHOW_SINGLE_GAMES_IN_COLLAPSE ? (
        <Box px={mdUp ? 3 : 2} pb={mdUp ? 3 : 2}>
          <EventCardSingleGames />
        </Box>
      ) : null}

      {props.showFooter ? props?.renderFooter?.() || <EventCardFooter /> : null}
    </Box>
  );
});
